import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../lib/auth';
import { useLang } from '../lib/lang';
import { expressStart, requestLoginCode, verifyLoginCode, createCheckout, getSettings } from '../lib/api';
import { redirectToPayment } from '../lib/paymentRedirect';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import PromoCodeInput from './PromoCodeInput';
import PayWithCryptoButton from './PayWithCryptoButton';
import PhoneInput from './PhoneInput';
import { ExpressIdentityFields, Field } from './ExpressIdentityFields';
import { toast } from 'sonner';
import { ArrowLeft, Ticket, Loader2, LogIn, Wallet, HelpCircle, Gift } from 'lucide-react';

// Multi-step passwordless participation flow for guests:
//   question → identity (guest form OR 6-digit code login) → payment
export default function ExpressParticipateModal({
  open, onOpenChange, raffle, qty, selectedPackage, pricePerEntryGBP, totalGBP,
  appliedPromo, setAppliedPromo, stripeEnabled, onLoggedIn,
}) {
  const { user, login } = useAuth();
  const { t } = useLang();
  const navigate = useNavigate();
  const hasQuestion = !!raffle?.question?.enabled;

  const [step, setStep] = useState(hasQuestion ? 'question' : 'identity');
  const [answer, setAnswer] = useState(null);
  const [questionError, setQuestionError] = useState('');
  const [mode, setMode] = useState('guest'); // 'guest' | 'login'
  const [form, setForm] = useState({ email: '', first_name: '', last_name: '', phone: '', country: '', postcode: '', address: '', city: '' });
  const [loginEmail, setLoginEmail] = useState('');
  const [code, setCode] = useState('');
  const [codeSent, setCodeSent] = useState(false);
  const [busy, setBusy] = useState(false);
  const [paying, setPaying] = useState(false);
  const [creditToUse, setCreditToUse] = useState(0);
  const [welcomeOffer, setWelcomeOffer] = useState(null);

  // Reset to first step whenever the modal opens
  useEffect(() => {
    if (open) {
      setStep(hasQuestion ? 'question' : (user ? 'payment' : 'identity'));
      setAnswer(null); setQuestionError(''); setMode('guest');
      setCode(''); setCodeSent(false); setLoginEmail(''); setCreditToUse(0);
      getSettings().then(r => setWelcomeOffer(r.data.settings?.welcome_offer || null)).catch(() => {});
    }
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const balance = Number(user?.points_balance || 0);
  const afterPromo = Math.max(0, Number(totalGBP) - Number(appliedPromo?.final_discount || 0));
  const welcomeEligible = !!welcomeOffer?.enabled && !!user && Number(user.total_entries || 0) === 0 && !user.welcome_offer_used;
  const welcomeDiscount = (welcomeEligible && Number(welcomeOffer.amount || 0) > 0 && afterPromo >= Number(welcomeOffer.min_spend || 0))
    ? Math.min(Number(welcomeOffer.amount), afterPromo) : 0;
  const afterWelcome = Math.max(0, afterPromo - welcomeDiscount);
  const finalToPay = Math.max(0, afterWelcome - Number(creditToUse || 0)).toFixed(2);

  const handleQuestionContinue = () => {
    if (answer === null) { setQuestionError(t('expSelectAnswer')); return; }
    if (Number(answer) !== Number(raffle.question.correct_answer)) {
      setQuestionError(t('expWrongAnswer')); setAnswer(null); return;
    }
    setQuestionError('');
    setStep(user ? 'payment' : 'identity');
  };

  const handleGuestSubmit = async () => {
    const phoneDigits = (form.phone || '').replace(/\D/g, '');
    if (!form.email.trim() || !form.first_name.trim() || phoneDigits.length < 6 || !form.address.trim() || !form.city.trim() || !form.postcode.trim() || !form.country.trim()) {
      toast.error(t('expFillRequired')); return;
    }
    setBusy(true);
    try {
      const res = await expressStart(form);
      login(res.data.token, res.data.user);
      onLoggedIn?.();
      setStep('payment');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur');
    } finally { setBusy(false); }
  };

  const handleSendCode = async () => {
    if (!loginEmail.trim()) { toast.error(t('expEmail')); return; }
    setBusy(true);
    try {
      await requestLoginCode({ email: loginEmail.trim() });
      setCodeSent(true);
      toast.success(t('expCodeSent'));
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur');
    } finally { setBusy(false); }
  };

  const handleVerifyCode = async () => {
    if (code.trim().length !== 6) { toast.error(t('expEnterCode')); return; }
    setBusy(true);
    try {
      const res = await verifyLoginCode({ email: loginEmail.trim(), code: code.trim() });
      login(res.data.token, res.data.user);
      onLoggedIn?.();
      toast.success(t('expWelcomeBack'));
      setStep('payment');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Code incorrect');
    } finally { setBusy(false); }
  };

  const handlePay = async () => {
    const remaining = Number(finalToPay);
    // Only require Stripe when there is a real amount to charge by card.
    if (remaining > 0 && !stripeEnabled) { toast.info(t('stripeComingSoon') || 'Paiements bientôt disponibles'); return; }
    setPaying(true);
    try {
      const payload = {
        type: 'raffle_direct', raffle_id: raffle._id, quantity: qty,
        origin_url: window.location.origin,
        credit_to_use: Number(creditToUse) || 0,
        promo_code: appliedPromo?.code || undefined,
      };
      if (selectedPackage !== null && selectedPackage !== undefined) payload.package_idx = selectedPackage;
      if (hasQuestion) payload.answer = String(answer);
      const res = await createCheckout(payload);
      if (res.data?.credit_only) {
        toast.success(t('participationConfirmed') || 'Participation enregistrée !');
        await onLoggedIn?.();
        onOpenChange(false);
        navigate('/account');
        setPaying(false);
        return;
      }
      const ok = await redirectToPayment(res.data);
      if (!ok) { toast.error('URL de paiement manquante'); setPaying(false); }
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erreur de paiement');
      setPaying(false);
    }
  };

  const StepDots = () => {
    const steps = [hasQuestion && 'question', 'identity', 'payment'].filter(Boolean);
    const idx = steps.indexOf(step);
    return (
      <div className="flex items-center justify-center gap-1.5 mt-1">
        {steps.map((s, i) => (
          <span key={s} className={`h-1.5 rounded-full transition-all ${i <= idx ? 'w-6 bg-gold' : 'w-1.5 bg-gold/30'}`} />
        ))}
      </div>
    );
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-white border-border max-h-[92vh] overflow-y-auto p-0" data-testid="express-participate-modal">
        <div className="sticky top-0 z-10 bg-gradient-to-b from-cream to-cream/95 backdrop-blur-sm border-b border-gold/20 px-5 py-4">
          <DialogHeader className="text-left">
            <DialogTitle className="font-display text-lg leading-tight flex items-center gap-2">
              <Ticket className="w-4 h-4 text-primary" />
              {step === 'question' ? t('expStepQuestion') : step === 'identity' ? t('expStepInfo') : t('expStepPay')}
            </DialogTitle>
            <DialogDescription className="text-xs text-rolex-green/65">{raffle?.title} · {qty} {qty > 1 ? t('tickets') : t('ticket')}</DialogDescription>
          </DialogHeader>
          <StepDots />
        </div>

        <div className="px-5 pt-4 pb-5 space-y-4">
          {/* ── STEP 1: QUESTION ── */}
          {step === 'question' && (
            <div className="space-y-4" data-testid="express-step-question">
              {/* Eligibility badge */}
              <span className="inline-flex items-center gap-1.5 rounded-full bg-rolex-green/5 border border-gold/30 px-3 py-1 text-[10px] uppercase tracking-[0.18em] font-bold text-rolex-green">
                <HelpCircle className="w-3.5 h-3.5 text-gold-dark" />
                {t('expAnswerQuestion')}
              </span>

              {/* Question card */}
              <div className="rounded-2xl bg-rolex-green-deep text-cream p-5 shadow-[0_10px_30px_rgba(20,40,30,0.18)]">
                {raffle.question.image && (
                  <img src={raffle.question.image} alt="" className="w-full max-h-32 object-contain rounded-xl bg-white/5 mb-3" />
                )}
                <p className="font-display text-lg font-bold leading-snug">{raffle.question.question}</p>
                <div className="mt-2 h-px w-12 bg-gold/60" />
              </div>

              {/* Answer options */}
              <div className="grid grid-cols-1 gap-2">
                {raffle.question.answers?.map((ans, idx) => {
                  const selected = answer === idx;
                  return (
                    <button key={idx} onClick={() => { setAnswer(idx); setQuestionError(''); }}
                      className={`group flex items-center gap-3 text-left px-4 py-3.5 rounded-xl border-2 text-sm transition-all duration-200 ${
                        selected
                          ? 'border-gold bg-gold/10 shadow-[0_4px_16px_rgba(184,138,42,0.18)]'
                          : 'border-stone-200 bg-white hover:border-gold/50 hover:-translate-y-0.5'
                      }`}
                      data-testid={`express-answer-${idx}`}>
                      <span className={`shrink-0 w-5 h-5 rounded-full border-2 flex items-center justify-center transition-colors ${
                        selected ? 'border-gold bg-gold' : 'border-stone-300 group-hover:border-gold/60'
                      }`}>
                        {selected && <span className="w-2 h-2 rounded-full bg-rolex-green" />}
                      </span>
                      <span className={`font-semibold ${selected ? 'text-rolex-green' : 'text-rolex-green/80'}`}>{ans}</span>
                    </button>
                  );
                })}
              </div>

              {questionError && <p className="text-red-500 text-xs font-medium" data-testid="express-question-error">{questionError}</p>}

              <Button className="w-full h-12 rounded-xl bg-primary text-primary-foreground hover:bg-primary/90 btn-press font-bold" onClick={handleQuestionContinue} data-testid="express-question-continue">
                {t('expContinue')}
              </Button>
            </div>
          )}

          {/* ── STEP 2: IDENTITY ── */}
          {step === 'identity' && (
            <div className="space-y-3" data-testid="express-step-identity">
              {mode === 'guest' ? (
                <>
                  <button type="button" onClick={() => { setMode('login'); setCodeSent(false); setCode(''); }}
                    className="w-full flex items-center justify-center gap-2 rounded-xl border-2 border-gold bg-gold/10 text-rolex-green font-bold text-sm py-3 hover:bg-gold/20 transition-colors" data-testid="express-switch-to-login">
                    <LogIn className="w-4 h-4 text-gold-dark" /> {t('expAlreadyClient')}
                  </button>
                  <div className="relative my-1 text-center">
                    <span className="bg-white px-3 text-[11px] uppercase tracking-widest text-rolex-green/45 relative z-10">{t('orDivider')}</span>
                    <div className="absolute inset-x-0 top-1/2 h-px bg-border" />
                  </div>
                  <ExpressIdentityFields form={form} setForm={setForm} />
                  <Button className="w-full h-12 rounded-xl bg-primary text-primary-foreground" onClick={handleGuestSubmit} disabled={busy} data-testid="express-guest-continue">
                    {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : t('expContinueToPay')}
                  </Button>
                  <p className="text-center text-[11px] text-rolex-green/55">{t('expNoPasswordHint')}</p>
                </>
              ) : (
                <>
                  <button type="button" onClick={() => setMode('guest')} className="w-full flex items-center justify-center gap-1.5 rounded-xl border border-border bg-cream/60 text-rolex-green font-semibold text-sm py-2.5 hover:bg-cream transition-colors" data-testid="express-switch-to-guest">
                    <ArrowLeft className="w-4 h-4" /> {t('expContinueAsGuest')}
                  </button>
                  <div className="text-center pt-2">
                    <h3 className="font-display text-lg font-bold">{t('expSignInTitle')}</h3>
                    <p className="text-xs text-rolex-green/65 mt-1">{t('expSignInDesc')}</p>
                  </div>
                  {!codeSent ? (
                    <>
                      <Field label={t('expEmail')} type="email" value={loginEmail} onChange={setLoginEmail} testid="express-login-email" />
                      <Button className="w-full h-12 rounded-xl bg-primary text-primary-foreground" onClick={handleSendCode} disabled={busy} data-testid="express-send-code">
                        {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : t('expSendCode')}
                      </Button>
                    </>
                  ) : (
                    <>
                      <Label className="text-sm">{t('expEnterCode')}</Label>
                      <Input value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                        inputMode="numeric" maxLength={6} placeholder="••••••"
                        className="rounded-xl text-center font-mono text-2xl tracking-[0.4em] h-14" data-testid="express-code-input" />
                      <Button className="w-full h-12 rounded-xl bg-primary text-primary-foreground" onClick={handleVerifyCode} disabled={busy} data-testid="express-verify-code">
                        {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : t('expVerify')}
                      </Button>
                      <button type="button" onClick={handleSendCode} className="w-full text-xs text-rolex-green/55 hover:text-rolex-green underline-offset-2 hover:underline">
                        {t('expResendCode')}
                      </button>
                    </>
                  )}
                </>
              )}
            </div>
          )}

          {/* ── STEP 3: PAYMENT ── */}
          {step === 'payment' && (
            <div className="space-y-3" data-testid="express-step-payment">
              {welcomeDiscount > 0 && (
                <div className="flex items-center gap-2 rounded-xl bg-rolex-green text-cream px-3 py-2.5 text-xs font-semibold" data-testid="express-welcome-badge">
                  <Gift className="w-4 h-4 text-gold shrink-0" />
                  <span>{(t('welcomeOfferApplied') || 'Offre de bienvenue appliquée')} : <strong className="text-gold">−£{welcomeDiscount.toFixed(0)}</strong> 🎉</span>
                </div>
              )}
              <div className="bg-gold-gradient text-rolex-green rounded-2xl p-4 shadow-lg flex items-center justify-between">
                <div>
                  <p className="text-[10px] uppercase tracking-[0.22em] font-extrabold opacity-80">{t('totalToPay') || 'Total à payer'}</p>
                  <p className="font-mono text-3xl font-black leading-none mt-1">£{finalToPay}</p>
                </div>
                <div className="text-right text-xs opacity-80">
                  <p className="font-mono">£{pricePerEntryGBP} × {qty}</p>
                  {appliedPromo?.final_discount > 0 && <p className="font-mono mt-0.5">−£{Number(appliedPromo.final_discount).toFixed(0)} promo</p>}
                  {welcomeDiscount > 0 && <p className="font-mono mt-0.5">−£{welcomeDiscount.toFixed(0)} {t('welcomeOfferShort') || 'bienvenue'}</p>}
                  {creditToUse > 0 && <p className="font-mono mt-0.5">−£{Number(creditToUse).toFixed(2)} crédit</p>}
                </div>
              </div>

              <PromoCodeInput variant="light" applied={appliedPromo} onApplied={setAppliedPromo} context={{ raffle_id: raffle._id, amount: Number(totalGBP) }} />

              {balance > 0 && (
                <button type="button"
                  onClick={() => setCreditToUse(creditToUse > 0 ? 0 : Math.min(balance, afterWelcome))}
                  className={`w-full flex items-center gap-3 rounded-xl border-2 p-3 transition-colors ${creditToUse > 0 ? 'border-emerald-500 bg-emerald-50' : 'border-stone-200 hover:border-emerald-300 bg-white'}`}
                  data-testid="express-use-credit-toggle">
                  <span className={`w-5 h-5 rounded-md border-2 flex items-center justify-center shrink-0 ${creditToUse > 0 ? 'bg-emerald-600 border-emerald-600' : 'border-stone-300 bg-white'}`}>
                    {creditToUse > 0 && <span className="text-white text-xs font-bold">✓</span>}
                  </span>
                  <Wallet className="w-4 h-4 text-emerald-600 shrink-0" />
                  <span className="text-sm text-left flex-1">
                    {t('useMyCredit') || 'Utiliser mon crédit'} : <strong className="text-emerald-700">£{balance.toFixed(2)}</strong>
                    {creditToUse > 0 && <span className="ml-1 text-emerald-700 font-bold">→ −£{Number(creditToUse).toFixed(2)}</span>}
                  </span>
                </button>
              )}

              <div className="space-y-2 sm:flex sm:gap-2 sm:space-y-0">
                <Button className="bg-primary text-primary-foreground hover:bg-primary/90 btn-press rounded-xl w-full h-14 text-base font-bold flex-1"
                  onClick={handlePay} disabled={paying} data-testid="express-pay-card">
                  {paying ? <Loader2 className="w-4 h-4 animate-spin" /> : (Number(finalToPay) === 0 ? (t('confirmParticipation') || 'Confirmer ma participation') : t('expPayByCard'))}
                </Button>
                {Number(finalToPay) > 0 && (
                  <PayWithCryptoButton
                    amountGbp={Number(finalToPay)}
                    payload={{
                      type: 'raffle_direct', raffle_id: raffle._id, quantity: qty,
                      credit_to_use: Number(creditToUse) || 0,
                      promo_code: appliedPromo?.code || undefined,
                      package_idx: selectedPackage !== null && selectedPackage !== undefined ? selectedPackage : undefined,
                      answer: hasQuestion ? String(answer) : undefined,
                    }}
                    disabled={paying}
                    className="rounded-xl w-full sm:w-auto h-14 text-base font-bold sm:flex-1"
                  />
                )}
              </div>
              <p className="text-center text-[11px] text-rolex-green/55">{t('securePaymentInfo')}</p>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
